'use client';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
  Switch,
} from '@heroui/react';
import { useTheme as useNextTheme } from 'next-themes';
import Link from 'next/link';

import { IconContainer } from '@/components/icons/IconsContainer';
import { LanguageSelector } from '@/components/languageSelector/LanguageSelector';
import ScrollToAnchor from '@/lib/utils/ScrollToAnchor';
import { useRouter, useSelectedLayoutSegment } from 'next/navigation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentLocale } from 'next-i18n-router/client';
import { i18nConfig } from './i18n/settings';
import {
  ChevronDown,
  EmailIcon,
  FlatColorIconsButtingIn3,
  InfoIcon2,
  ManuallyIcon,
  MoonIcon,
  SunIcon,
} from '../components/icons/Icons';

const icons = {
  chevron: <ChevronDown fill="currentColor" size={16} />,
  email: <EmailIcon className="text-primary" fill="currentColor" size={30} />,
  manually: <ManuallyIcon className="text-secondary" fill="currentColor" size={30} />,
  infoIcon2: <InfoIcon2 className="text-primary" fill="currentColor" size={30} />,
};

interface MenuItem {
  name?: string;
  href?: string;
  description?: string;
  subItems?: MenuItem[];
  category?: string;
  icon?: React.ReactNode;
  onlyMobile?: boolean;
  segment?: string;
}

export default function Nav({ tagline }: { tagline: string }) {
  const router = useRouter();
  const segment = useSelectedLayoutSegment();
  const locale = useCurrentLocale(i18nConfig) || i18nConfig.defaultLocale;
  const basePath = locale === i18nConfig.defaultLocale ? '' : `/${locale}`;
  const homePath = `${basePath}/`;
  const { theme, setTheme } = useNextTheme();
  const [themehelper] = useState(true);
  // const isDark = themehelper === 'dark' ? true : false;
  const [isMenuOpen, setIsMenuOpen] = useState<boolean | undefined>(false);
  // const locale = useCurrentLocale(i18nConfig);
  // console.log('Locale', locale);
  const { t } = useTranslation();

  const menuItems: MenuItem[] = [
    {
      name: t('nav.home'),
      href: homePath,
      segment: '',
    },
    {
      name: t('nav.why-now'),
      href: `${homePath}#why-now`,
      segment: '',
    },
    {
      name: t('nav.offers'),
      href: `${homePath}#offers`,
      segment: '',
    },
    {
      name: t('nav.method'),
      href: `${homePath}#method`,
      segment: '',
    },
    {
      name: `${t('contact.title')}`,
      href: `${homePath}#contact`,
      segment: '',
    },
  ];

  return (
    <div className="container mx-auto max-sm:fixed max-sm:top-0 max-sm:left-0 max-sm:right-0 max-sm:z-50">
      <ScrollToAnchor />
      <Navbar
        position="sticky"
        maxWidth={'xl'}
        className="flex-auto grow-1 justify-between mt-0 sm:mt-6 sm:rounded overflow-visible"
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
      >
        <NavbarContent>
          <NavbarMenuToggle
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            className="sm:hidden"
            // onChange={(e) => setIsMenuOpen(isMenuOpen ? false : true)}
            // showIn="sm"
          />
          <NavbarBrand
            className="flex items-center cursor-pointer"
            onClick={() => {
              setIsMenuOpen(false);
              router.push(homePath);
            }}
          >
            <span className="mr-2 lg:mr-0">
              <IconContainer
                Icon={FlatColorIconsButtingIn3}
                iconProps={{
                  className: 'lg:border lg:rounded bg-white',
                }}
              />
            </span>
            {/* <div className="flex-shrink-0">
              <BetaIcon isDark={isDark} />
            </div> */}
            <div className="lg:ml-1">C. Zeltner</div>
            <div className="hidden lg:block text-sm">&nbsp;-&nbsp;</div>
            <div className="hidden lg:block text-sm">{tagline}</div>
          </NavbarBrand>
        </NavbarContent>

        <NavbarContent className="ml-16 hidden sm:flex text-sm" justify="center">
          {menuItems.map((item, idx) => {
            if (item.subItems) {
              return (
                <Dropdown key={idx}>
                  <NavbarItem>
                    <DropdownTrigger>
                      <Button
                        disableRipple
                        className={`p-0 bg-transparent data-[hover=true]:bg-transparent ${
                          segment === item.segment ? 'font-bold' : ''
                        }`}
                        endContent={icons.chevron}
                        radius="sm"
                        variant="light"
                      >
                        {item.name}
                      </Button>
                    </DropdownTrigger>
                  </NavbarItem>
                  <DropdownMenu
                    aria-label={item.name}
                    className="w-[340px]"
                    itemClasses={{
                      base: 'gap-4',
                    }}
                  >
                    <DropdownSection title={item.name}>
                      {item.subItems.map((subItem, idx) => {
                        return (
                          <DropdownItem
                            key={idx}
                            description={subItem.description}
                            textValue={subItem.name}
                            startContent={subItem.icon}
                            onPress={() => {
                              router.push(`${subItem.href}`);
                            }}
                            closeOnSelect
                          >
                            {subItem.name}
                          </DropdownItem>
                        );
                      })}
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              );
            } else {
              return (
                <NavbarItem isActive={segment === item.segment} key={idx}>
                  <Link className="text-inherit" href={item.href!}>
                    {item.name}
                  </Link>
                </NavbarItem>
              );
            }
          })}
        </NavbarContent>

        <NavbarContent className="hidden sm:flex gap-1" justify="end">
          {themehelper && (
            <Switch
              //   thumbIcon={<MoonIcon />}
              isSelected={theme === 'dark'}
              onValueChange={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              startContent={<MoonIcon />}
              endContent={<SunIcon />}
            />
          )}
        </NavbarContent>

        <NavbarContent className="hidden md:flex gap-1" justify="end">
          <LanguageSelector />
        </NavbarContent>

        <NavbarMenu className="mt-7 overflow-visible">
          {menuItems.map((item, idx) => {
            if (item.subItems) {
              return (
                <Dropdown key={idx}>
                  <NavbarMenuItem>
                    <DropdownTrigger>
                      <Button
                        disableRipple
                        className={`p-0 bg-transparent data-[hover=true]:bg-transparent ${
                          segment === item.segment ? 'font-bold' : ''
                        }`}
                        endContent={icons.chevron}
                        radius="sm"
                        variant="light"
                      >
                        {item.name}
                      </Button>
                    </DropdownTrigger>
                  </NavbarMenuItem>
                  <DropdownMenu
                    aria-label={item.name}
                    className="w-[340px]"
                    itemClasses={{
                      base: 'gap-4',
                    }}
                    // onAction={(key) => alert(key)}
                  >
                    <DropdownSection title={item.name}>
                      {item.subItems.map((subItem, idx) => {
                        return (
                          <DropdownItem
                            key={idx}
                            description={subItem.description}
                            textValue={subItem.name}
                            startContent={subItem.icon}
                            onPress={() => {
                              setIsMenuOpen(false);
                              router.push(`${subItem.href}`);
                            }}
                            closeOnSelect
                          >
                            {subItem.name}
                          </DropdownItem>
                        );
                      })}
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              );
            } else {
              return (
                <NavbarMenuItem key={`${item.name}`}>
                  {/* <Button // auto
                    variant="flat"
                    onPress={() => {
                      setIsMenuOpen(!isMenuOpen);
                      router.push(`${item.href}`);
                    }}
                  >
                    {item.name}
                  </Button> */}
                  <Link
                    className={`w-full ${segment === item.segment ? 'font-bold' : 'font-normal'}`}
                    href={item.href!}
                    onClick={() => {
                      setIsMenuOpen(false);
                    }}
                  >
                    {item.name}
                  </Link>
                </NavbarMenuItem>
              );
            }
          })}
          <NavbarMenuItem key="themeswitcher-m">
            {themehelper && (
              <Switch
                //   thumbIcon={<MoonIcon />}
                isSelected={theme === 'dark'}
                onValueChange={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                startContent={<MoonIcon />}
                endContent={<SunIcon />}
              />
            )}
          </NavbarMenuItem>
          <NavbarMenuItem key="languageselector-m">
            <LanguageSelector />
          </NavbarMenuItem>
        </NavbarMenu>
      </Navbar>
    </div>
  );
}
